import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { requestThrottler } from '@/utils/requestThrottle';
import { dlog } from '@/utils/devlog';

const withTimeout = async (promise, ms, errorMsg) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMsg)), ms);
  });
  try {
    const result = await Promise.race([promise, timeoutPromise]);
    return result;
  } finally {
    clearTimeout(timeoutId);
  }
};

interface UserProfile {
  is_admin: boolean;
  override_license_check: boolean;
  keap_tags?: string[];
}
interface ApiKeys {
  elevenlabs?: string;
  openai?: string;
  grok?: string;
  xai?: string;
  vapi?: string;
  twilio?: string;
  twilioSid?: string;
  twilioAuthToken?: string;
  zapierWebhook?: string;
  makeWebhook?: string;
  webhookUrl?: string;
  calendlyUrl?: string;
  googleCalendarUrl?: string;
  resend_api_key?: string;
  [key: `vapi_${string}`]: string;
}
interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  apiKeys: ApiKeys | null;
  loading: boolean;
  hasInitialLoad: React.MutableRefObject<boolean>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  isAdmin: boolean;
  isOnlyMember: boolean;
  clientIds: string[];
  refreshProfile: () => Promise<void>;
  refreshApiKeys: () => Promise<void>;
  isPortalLogin: boolean;
  setIsPortalLogin: (val: boolean) => void;
  isAuthReady: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Cache configuration
const PROFILE_CACHE_KEY = 'auth_profile_cache';
const API_KEYS_CACHE_KEY = 'auth_api_keys_cache';
const CLIENT_IDS_CACHE_KEY = 'auth_client_ids_cache';
const PROFILE_CACHE_TTL = 30 * 60 * 1000; // 30 minutes
const API_KEYS_CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const CACHE_VERSION = 1;
// Debug/stress test modes
const STRESS_TEST_MODE = {
  slowProfileFetch: false,
  failedDbFetch: false,
  missingSession: false,
  corruptCache: false,
};
// dlog('[auth:init]', 'AuthContext module loaded');
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKeys | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [isOnlyMember, setIsOnlyMember] = useState(false);
  const [isPortalLogin, setIsPortalLogin] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const hasInitialLoad = useRef(false);
  const authTokenKey = `sb-${
    import.meta.env.VITE_SUPABASE_PROJECT_ID
  }-auth-token`; // Moved here for reuse
  // Refresh user data to ensure user_metadata is up-to-date
  const refreshUser = async () => {
    try {
      const { data: userData, error } = await withTimeout(
        supabase.auth.getUser(),
        5000,
        '[AuthContext] User refresh timeout',
      );
      if (error) {
        // console.error('[AuthContext] Error refreshing user:', error);
        // dlog('[AuthContext] Error refreshing user', { error });
        setUser(null);
        return;
      }
      // console.log('[AuthContext] Refreshed user:', {
      //   id: userData.user?.id,
      //   email: userData.user?.email,
      //   metadata: userData.user?.user_metadata,
      // });
      // dlog('[AuthContext] Refreshed user', {
      //   id: userData.user?.id,
      //   metadata: userData.user?.user_metadata,
      // });
      setUser(userData.user ?? null);
    } catch (err) {
      console.error('[AuthContext] Unexpected error refreshing user:', err);
      // dlog('[AuthContext] Unexpected error refreshing user', { error: err });
    }
  };
  // Get cached profile
  const getCachedProfile = (userId: string): UserProfile | null => {
    try {
      // console.log(`[AuthContext] Checking cache for user:`, userId);
      if (STRESS_TEST_MODE.corruptCache) {
        // console.log(`[AuthContext] STRESS TEST: Simulating corrupt cache`);
        throw new Error('Simulated corrupt sessionStorage');
      }
      const cached = sessionStorage.getItem(PROFILE_CACHE_KEY);
      if (!cached) {
        // console.log(`[AuthContext] No cached profile found`);
        return null;
      }
      let data: any;
      try {
        data = JSON.parse(cached);
      } catch (parseError) {
        console.error(
          `[AuthContext] Failed to parse cached profile, clearing cache:`,
          parseError,
        );
        sessionStorage.removeItem(PROFILE_CACHE_KEY);
        return null;
      }
      const now = Date.now();
      if (now - data.timestamp > PROFILE_CACHE_TTL || data.userId !== userId) {
        // console.log(
        //   `[AuthContext] Cached profile expired or wrong user - clearing cache`
        // );
        sessionStorage.removeItem(PROFILE_CACHE_KEY);
        return null;
      }
      // console.log(`[AuthContext] Using cached profile for user:`, userId);
      return data.profile;
    } catch (error) {
      console.error(
        `[AuthContext] Unexpected error reading cached profile:`,
        error,
      );
      sessionStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }
  };
  // Get cached profile timestamp
  const getCachedProfileTimestamp = (userId: string): number | null => {
    try {
      const cached = sessionStorage.getItem(PROFILE_CACHE_KEY);
      if (!cached) return null;
      const data: any = JSON.parse(cached);
      if (data.userId !== userId) return null;
      return data.timestamp;
    } catch (error) {
      return null;
    }
  };
  // Get cached API keys
  const getCachedApiKeys = (userId: string): ApiKeys | null => {
    try {
      // console.log(`[AuthContext] Checking API keys cache for user:`, userId);
      const cacheKey = `${API_KEYS_CACHE_KEY}_${userId}`;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) {
        // console.log(`[AuthContext] No cached API keys found`);
        return null;
      }
      const data: any = JSON.parse(cached);
      const now = Date.now();
      if (data.version !== CACHE_VERSION) {
        // console.log(`[AuthContext] Cache version mismatch, clearing cache`);
        localStorage.removeItem(cacheKey);
        return null;
      }
      if (now - data.timestamp > API_KEYS_CACHE_TTL || data.userId !== userId) {
        // console.log(
        //   `[AuthContext] API keys cache expired or wrong user - clearing cache`
        // );
        localStorage.removeItem(cacheKey);
        return null;
      }
      // console.log(`[AuthContext] Using cached API keys for user:`, userId);
      return data.apiKeys;
    } catch (error) {
      console.error(`[AuthContext] Error reading cached API keys:`, error);
      return null;
    }
  };
  // Set cached API keys
  const setCachedApiKeys = (apiKeys: ApiKeys, userId: string) => {
    try {
      const data: any = {
        apiKeys,
        timestamp: Date.now(),
        userId,
        version: CACHE_VERSION,
      };
      const cacheKey = `${API_KEYS_CACHE_KEY}_${userId}`;
      localStorage.setItem(cacheKey, JSON.stringify(data));
      // console.log(`[AuthContext] API keys cached for user:`, userId);
    } catch (error) {
      console.error('[AuthContext] Error caching API keys:', error);
    }
  };
  // Get cached client IDs
  const getCachedClientIds = (
    userId: string,
  ): { clientIds: string[]; isOnlyMember: boolean } | null => {
    try {
      // console.log(`[AuthContext] Checking client IDs cache for user:`, userId);
      const cacheKey = `${CLIENT_IDS_CACHE_KEY}_${userId}`;
      const cached = localStorage.getItem(cacheKey);
      if (!cached) {
        // console.log(`[AuthContext] No cached client IDs found`);
        return null;
      }
      const data: any = JSON.parse(cached);
      const now = Date.now();
      if (now - data.timestamp > PROFILE_CACHE_TTL || data.userId !== userId) {
        // console.log(
        //   `[AuthContext] Client IDs cache expired or wrong user - clearing cache`
        // );
        localStorage.removeItem(cacheKey);
        return null;
      }
      // console.log(`[AuthContext] Using cached client IDs for user:`, userId);
      return { clientIds: data.clientIds, isOnlyMember: data.isOnlyMember };
    } catch (error) {
      console.error(`[AuthContext] Error reading cached client IDs:`, error);
      return null;
    }
  };
  // Set cached client IDs
  const setCachedClientIds = (
    clientIds: string[],
    isOnlyMember: boolean,
    userId: string,
  ) => {
    try {
      const data = {
        clientIds,
        isOnlyMember,
        timestamp: Date.now(),
        userId,
      };
      const cacheKey = `${CLIENT_IDS_CACHE_KEY}_${userId}`;
      localStorage.setItem(cacheKey, JSON.stringify(data));
      // console.log(`[AuthContext] Client IDs cached for user:`, userId);
    } catch (error) {
      console.error('[AuthContext] Error caching client IDs:', error);
    }
  };
  // Clear API keys cache
  const clearApiKeysCache = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(API_KEYS_CACHE_KEY) && localStorage.getItem(key)) {
          localStorage.removeItem(key);
          // console.log(`[AuthContext] Cleared API keys cache:`, key);
        }
      });
    } catch (error) {
      console.error('[AuthContext] Error clearing API keys cache:', error);
    }
  };
  // Clear client IDs cache
  const clearClientIdsCache = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(CLIENT_IDS_CACHE_KEY) && localStorage.getItem(key)) {
          localStorage.removeItem(key);
          // console.log(`[AuthContext] Cleared client IDs cache:`, key);
        }
      });
    } catch (error) {
      console.error('[AuthContext] Error clearing client IDs cache:', error);
    }
  };
  // Clear any unnecessary or legacy caches (e.g., from prior changes)
  const clearLegacyCaches = () => {
    try {
      const legacyKeys = ['business_portal_', 'client_memberships_']; // Add any other suspect keys here
      Object.keys(localStorage).forEach((key) => {
        legacyKeys.forEach((prefix) => {
          if (key.startsWith(prefix)) {
            localStorage.removeItem(key);
            // console.log(`[AuthContext] Cleared legacy cache:`, key);
          }
        });
      });
    } catch (error) {
      console.error('[AuthContext] Error clearing legacy caches:', error);
    }
  };
  // Set cached profile
  const setCachedProfile = (profile: UserProfile, userId: string) => {
    try {
      const data: any = {
        profile,
        timestamp: Date.now(),
        userId,
      };
      sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(data));
      // console.log(`[AuthContext] Profile cached for user:`, userId);
    } catch (error) {
      // console.error(`[AuthContext] Error caching profile:`, error);
    }
  };
  // Clear profile cache
  const clearProfileCache = () => {
    try {
      if (sessionStorage.getItem(PROFILE_CACHE_KEY)) {
        sessionStorage.removeItem(PROFILE_CACHE_KEY);
        // console.log(`[AuthContext] Profile cache cleared`);
      }
      requestThrottler.clearPattern('profile-');
    } catch (error) {
      console.error('[AuthContext] Error clearing profile cache:', error);
    }
  };
  // Fetch user profile
  const fetchProfile = async (
    userId: string,
    retryCount: number = 0,
  ): Promise<UserProfile> => {
    // dlog('[profile:refresh]', 'start', { userId, retryCount });
    // console.log(
    //   `Profile fetch started for user: ${userId} (attempt ${retryCount + 1})`
    // );
    if (retryCount === 0) {
      const cached = getCachedProfile(userId);
      if (cached) {
        // dlog('[profile:refresh]', 'cached', {
        //   partial: (cached as any)?._partial === true,
        // });
        // console.log(`Profile fetch success - using cached profile`);
        const cacheAge = Date.now() - (getCachedProfileTimestamp(userId) || 0);
        if (cacheAge < PROFILE_CACHE_TTL / 2) {
          return cached;
        }
        setTimeout(() => fetchProfile(userId, 1), 0);
        return cached;
      }
    }
    return requestThrottler.throttle(
      `profile-${userId}`,
      async () => {
        try {
          const { data, error } = await withTimeout(
            supabase
              .from('profiles')
              .select('is_admin, override_license_check, keap_tags')
              .eq('user_id', userId)
              .maybeSingle(),
            5000,
            '[AuthContext] Profile DB query timeout',
          );
          if (error) {
            // dlog('[profile:refresh]', 'error', error);
            console.error(`Database error fetching profile:`, error);
            const cached = getCachedProfile(userId);
            if (cached) {
              // console.log(`Using cached profile due to fetch error`);
              return cached;
            }
            throw error;
          }
          const profileData: UserProfile = {
            is_admin: data?.is_admin || false,
            override_license_check: data?.override_license_check || false,
            keap_tags: data?.keap_tags || [],
          };
          // dlog('[profile:refresh]', 'success', { partial: false });
          // console.log(`Profile fetch success:`, profileData);
          setCachedProfile(profileData, userId);
          return profileData;
        } catch (error) {
          const cached = getCachedProfile(userId);
          if (cached) {
            // console.log(`Using cached profile due to fetch error:`, error);
            return cached;
          }
          // console.log(
          //   `Profile fetch failed, returning fallback for user: ${userId}`,
          //   error
          // );
          const fallbackProfile: UserProfile = {
            is_admin: false,
            override_license_check: false,
            keap_tags: [],
          };
          return fallbackProfile;
        }
      },
      { ttl: 100 },
    );
  };
  // Fetch client memberships
  const fetchClientMemberships = async (userId: string) => {
    try {
      const cacheKey = `${CLIENT_IDS_CACHE_KEY}_${userId}`;
      const cached = getCachedClientIds(userId);
      if (cached) {
        // console.log('[AuthContext] Using cached client memberships');
        return cached;
      }
      // Fetch memberships
      const { data: membershipData, error: membershipError } =
        await withTimeout(
          supabase
            .from('client_memberships')
            .select('client_id, role')
            .eq('user_id', userId),
          5000,
          '[AuthContext] Client memberships query timeout',
        );
      if (membershipError) {
        console.error(
          '[AuthContext] Error fetching client memberships:',
          membershipError,
        );
        // dlog('[AuthContext] Error fetching client memberships', {
        //   error: membershipError,
        // });
        return { clientIds: [], isOnlyMember: false };
      }
      // Fetch owned clients to check for owner role
      const { data: ownedData, error: ownedError } = await withTimeout(
        supabase.from('clients').select('id').eq('user_id', userId),
        5000,
        '[AuthContext] Owned clients query timeout',
      );
      if (ownedError) {
        console.error(
          '[AuthContext] Error fetching owned clients:',
          ownedError,
        );
        // dlog('[AuthContext] Error fetching owned clients', {
        //   error: ownedError,
        // });
        return { clientIds: [], isOnlyMember: false };
      }
      const clientIds = membershipData
        .map((membership) => membership.client_id)
        .filter(Boolean);
      const isOnlyMember =
        membershipData.length > 0 &&
        membershipData.every((membership) => {
          const role = membership.role?.toLowerCase();
          return role === 'member';
        });
      const hasOwnerRole =
        ownedData.length > 0 ||
        membershipData.some((membership) => {
          const role = membership.role?.toLowerCase();
          return role === 'owner';
        });
      // console.log('[AuthContext] Client memberships:', {
      //   clientIds,
      //   isOnlyMember,
      //   hasOwnerRole,
      //   membershipData,
      //   ownedData,
      // });
      // console.log(
      //   '[AuthContext] Computed isOnlyMember:',
      //   isOnlyMember,
      //   'hasOwnerRole:',
      //   hasOwnerRole,
      //   'is_admin:',
      //   profile?.is_admin
      // );
      // dlog('[AuthContext] Client memberships', {
      //   clientIds,
      //   isOnlyMember,
      //   hasOwnerRole,
      //   membershipData,
      //   ownedData,
      // });
      const result = {
        clientIds,
        isOnlyMember: isOnlyMember && !hasOwnerRole && !profile?.is_admin,
      };
      setCachedClientIds(clientIds, result.isOnlyMember, userId);
      return result;
    } catch (error) {
      console.error(
        '[AuthContext] Unexpected error fetching client memberships:',
        error,
      );
      // dlog('[AuthContext] Unexpected error fetching client memberships', {
      //   error,
      // });
      return { clientIds: [], isOnlyMember: false };
    }
  };
  // Fetch user API keys
  const fetchApiKeys = async (
    userId: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<ApiKeys | null> => {
    try {
      // console.log(`[AuthContext] Fetching API keys for user:`, userId, options);
      if (!options.forceRefresh) {
        const cached = getCachedApiKeys(userId);
        if (cached) {
          // console.log('[AuthContext] Using cached API keys:', cached);
          return cached;
        }
      }
      return requestThrottler.throttle(
        `api-keys-${userId}`,
        async () => {
          // console.log(`[AuthContext] Fetching fresh API keys from database`);
          const { data, error } = await withTimeout(
            supabase.functions.invoke('manage-api-keys', {
              body: { action: 'get' },
            }),
            5000,
            '[AuthContext] API keys edge function timeout',
          );
          if (error) {
            console.error(
              '[AuthContext] Error fetching API keys from edge function:',
              error,
            );
            return null;
          }
          // console.log(`[AuthContext] Raw API keys from edge function:`, data);
          const apiKeysData: ApiKeys = {};
          (data?.keys || []).forEach((key: any) => {
            const { provider, key_name, decrypted_value } = key;
            if (provider === 'twilio') {
              if (key_name === 'account_sid') {
                apiKeysData.twilioSid = decrypted_value;
                apiKeysData.twilio = decrypted_value;
              }
              if (key_name === 'auth_token') {
                apiKeysData.twilioAuthToken = decrypted_value;
              }
            } else if (provider === 'calendly') {
              if (key_name === 'scheduling_link') {
                apiKeysData.calendlyUrl = decrypted_value;
              }
            } else if (provider === 'webhook') {
              if (key_name === 'url' || key_name === 'webhook_url') {
                apiKeysData.webhookUrl = decrypted_value;
              }
            } else if (provider === 'zapier') {
              if (key_name === 'webhook_url') {
                apiKeysData.zapierWebhook = decrypted_value;
              }
            } else if (provider === 'make') {
              if (key_name === 'webhook_url') {
                apiKeysData.makeWebhook = decrypted_value;
              }
            } else if (provider === 'google_calendar') {
              if (key_name === 'calendar_url') {
                apiKeysData.googleCalendarUrl = decrypted_value;
              }
            } else if (provider === 'grok' || provider === 'xai') {
              apiKeysData.grok = decrypted_value;
              apiKeysData.xai = decrypted_value;
            } else if (provider === 'vapi') {
              if (key_name === 'api_key') {
                apiKeysData.vapi = decrypted_value;
              } else if (key_name.startsWith('account_')) {
                apiKeysData[`vapi_${key_name}`] = decrypted_value;
              }
            } else {
              apiKeysData[provider] = decrypted_value;
            }
          });

          if (!apiKeysData.webhookUrl) {
            if (apiKeysData.zapierWebhook) {
              apiKeysData.webhookUrl = apiKeysData.zapierWebhook;
            } else if (apiKeysData.makeWebhook) {
              apiKeysData.webhookUrl = apiKeysData.makeWebhook;
            }
          }

          // console.log('[Twilio Keys Debug]', {
          //   sid: apiKeysData.twilioSid,
          //   token: apiKeysData.twilioAuthToken,
          //   legacy: apiKeysData.twilio,
          // });
          // console.log(`[AuthContext] Transformed API keys:`, apiKeysData);

          setCachedApiKeys(apiKeysData, userId);
          return apiKeysData;
        },
        { ttl: API_KEYS_CACHE_TTL },
      );
    } catch (error) {
      // console.error(`[AuthContext] Error fetching API keys:`, error);
      return null;
    }
  };

  // Refresh API keys
  const refreshApiKeys = async () => {
    if (!user?.id && !session?.user?.id) {
      // console.log('[AuthContext] No user ID available for refreshApiKeys');
      return;
    }
    const userId = user?.id || session?.user?.id!;
    // console.log(`[AuthContext] Refreshing API keys for user:`, userId);
    requestThrottler.clearPattern(`api-keys-${userId}`);
    const apiKeysData = await fetchApiKeys(userId, { forceRefresh: true });
    setApiKeys(apiKeysData);
    // console.log(`[AuthContext] API keys refreshed:`, apiKeysData);
  };

  // Refresh profile
  const refreshProfile = async () => {
    if (!user) {
      // console.log(`[AuthContext] No user to refresh profile for`);
      return;
    }
    // console.log(`Refreshing profile for user:`, user.id);
    clearProfileCache();
    try {
      const profileData = await fetchProfile(user.id, 1);
      setProfile(profileData);
    } catch (error) {
      // console.error(`Profile refresh failed, using fallback:`, error);
      setProfile({
        is_admin: false,
        override_license_check: false,
        keap_tags: [],
      });
    }
  };

  // NEW: Handle session refresh in a useEffect to avoid async calls in onAuthStateChange
  useEffect(() => {
    if (!session?.expires_at) return;

    const expiryTime = session.expires_at * 1000;
    const buffer = 5 * 60 * 1000; // 5 min buffer
    if (expiryTime < Date.now() + buffer) {
      const refresh = async () => {
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error) throw error;
          setSession(data.session);
          setUser(data.session?.user ?? null);
          // dlog('[auth:refresh]', 'manual refresh success');
        } catch (err) {
          console.error('[AuthContext] Manual refresh failed:', err);
          // Optionally handle logout or retry logic here
        }
      };
      refresh();
    }
  }, [session]); // Trigger on session changes

  // Handle auth state changes (no async Supabase calls here to avoid deadlock bug)
  useEffect(() => {
    // dlog('[auth:onAuthStateChange]', 'subscribe');
    let isMounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // console.log('[AuthContext] onAuthStateChange triggered:', {
      //   event,
      //   userId: newSession?.user?.id,
      //   email: newSession?.user?.email,
      //   pathname: window.location.pathname,
      // });
      // dlog('[AuthContext]', {
      //   event,
      //   user: newSession?.user?.id,
      //   metadata: newSession?.user?.user_metadata,
      //   pathname: window.location.pathname,
      // });

      if (!isMounted) return;

      // Use newSession directly (no await getSession() or refreshSession() here)
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // NEW: Clear token if no session but token exists
      const storedToken = localStorage.getItem(authTokenKey);
      if (!newSession && storedToken) {
        // console.warn('[AuthContext] Null session despite token - clearing');
        localStorage.removeItem(authTokenKey);
        clearApiKeysCache();
        clearClientIdsCache();
        clearProfileCache();
        clearLegacyCaches();
        toast({
          variant: 'destructive',
          title: 'Invalid Session',
          description: 'Cleared invalid auth token. Please log in again.',
        });
        setLoading(false);
        setIsAuthReady(false);
        return;
      }

      if (newSession?.user) {
        const userId = newSession.user.id;
        dlog('[auth:state]', 'user found', { uid: userId });
        hasInitialLoad.current = true;

        // Parallel fetch profile, memberships, and API keys (non-blocking)
        Promise.all([
          fetchProfile(userId),
          fetchClientMemberships(userId),
          !isOnlyMember ? fetchApiKeys(userId) : Promise.resolve(null),
        ])
          .then(([profileData, { clientIds, isOnlyMember }, apiKeysData]) => {
            if (isMounted) {
              setProfile(profileData);
              setClientIds(clientIds);
              setIsOnlyMember(isOnlyMember && !profileData.is_admin);
              setApiKeys(apiKeysData);
              setLoading(false);
              setIsAuthReady(true);
            }
          })
          .catch((err) => {
            console.error('[AuthContext] Parallel fetch error:', err);
            if (isMounted) {
              setLoading(false);
              setIsAuthReady(true); // Set ready even on error to unblock
            }
          });
      } else {
        setProfile(null);
        setApiKeys(null);
        setClientIds([]);
        setIsOnlyMember(false);
        setLoading(false);
        setIsAuthReady(true);
        hasInitialLoad.current = true;
      }

      if (event === 'SIGNED_IN' && newSession?.user) {
        const userId = newSession.user.id;
        // console.log(
        //   '[AuthContext] Do we have initial load? ' + hasInitialLoad.current
        // );
        let existingUserId = user?.id;
        if (hasInitialLoad.current && existingUserId === userId) {
          // console.log(
          //   `[AuthContext] Skipping duplicate profile fetch on SIGNED_IN - already loaded for user:`,
          //   userId
          // );
          return;
        }
        // console.log(
        //   `[AuthContext] SIGNED_IN event - fetching profile and refreshing user for:`,
        //   userId
        // );
        refreshUser(); // Fire-and-forget
        const profileDataPromise = fetchProfile(userId);
        profileDataPromise.then((profileData) => {
          if (isMounted) {
            setProfile(profileData);
          }
        });
        let cachedClientIds = getCachedClientIds(userId);
        if (isPortalLogin) {
          requestThrottler.clearPattern(`profile-${userId}`);
          clearClientIdsCache();
          const membershipsPromise = fetchClientMemberships(userId);
          membershipsPromise.then(({ clientIds, isOnlyMember }) => {
            if (isMounted) {
              setClientIds(clientIds);
              setIsOnlyMember(isOnlyMember && !profile?.is_admin);
              // console.log(
              //   `[AuthContext] Client memberships force refreshed for portal login:`,
              //   {
              //     clientIds,
              //     isOnlyMember,
              //   }
              // );
            }
          });
        } else if (cachedClientIds) {
          setClientIds(cachedClientIds.clientIds);
          setIsOnlyMember(cachedClientIds.isOnlyMember && !profile?.is_admin);
          // console.log(
          //   `[AuthContext] Using cached client memberships:`,
          //   cachedClientIds
          // );
        } else {
          const membershipsPromise = fetchClientMemberships(userId);
          membershipsPromise.then(({ clientIds, isOnlyMember }) => {
            if (isMounted) {
              setClientIds(clientIds);
              setIsOnlyMember(isOnlyMember && !profile?.is_admin);
            }
          });
        }
        // console.log(
        //   '[AuthContext] Checking API keys fetch condition on SIGNED_IN:',
        //   { isOnlyMember, is_admin: profile?.is_admin }
        // );
        if (!isOnlyMember) {
          const apiKeysPromise = fetchApiKeys(userId);
          apiKeysPromise
            .then((apiKeysData) => {
              if (isMounted) {
                setApiKeys(apiKeysData);
                // console.log(`[AuthContext] API keys loaded on sign in`);
              }
            })
            .catch((error) => {
              if (isMounted) {
                setApiKeys(null);
                // console.log(`[AuthContext] API keys failed to load:`, error);
              }
            });
        } else {
          setApiKeys(null);
          // console.log(`[AuthContext] Skipping API keys fetch for member-only`);
        }
      } else if (event === 'SIGNED_OUT') {
        if (isSigningOut) return;
        setIsSigningOut(true);
        // console.log(`[AuthContext] SIGNED_OUT event - clearing user data`);
        setProfile(null);
        setApiKeys(null);
        setClientIds([]);
        setIsOnlyMember(false);
        localStorage.removeItem(authTokenKey);
        clearApiKeysCache();
        clearClientIdsCache();
        clearProfileCache();
        clearLegacyCaches();
        setIsSigningOut(false);
        setLoading(false);
        setIsAuthReady(true);
      } else if (event === 'USER_UPDATED' && newSession?.user) {
        // console.log(
        //   `[AuthContext] USER_UPDATED event - refreshing user data:`,
        //   newSession?.user
        // );
        refreshUser(); // Fire-and-forget
        const userId = newSession.user.id;
        if (userId) {
          const profilePromise = fetchProfile(userId);
          profilePromise.then((profileData) => {
            if (isMounted) {
              setProfile(profileData);
            }
          });
          const cachedClientIds = getCachedClientIds(userId);
          if (cachedClientIds) {
            setClientIds(cachedClientIds.clientIds);
            setIsOnlyMember(cachedClientIds.isOnlyMember && !profile?.is_admin);
            // console.log(
            //   `[AuthContext] Using cached client memberships:`,
            //   cachedClientIds
            // );
          } else {
            const membershipsPromise = fetchClientMemberships(userId);
            membershipsPromise.then(({ clientIds, isOnlyMember }) => {
              if (isMounted) {
                setClientIds(clientIds);
                setIsOnlyMember(isOnlyMember && !profile?.is_admin);
              }
            });
          }
          // console.log(
          //   '[AuthContext] Checking API keys fetch condition on USER_UPDATED:',
          //   { isOnlyMember, is_admin: profile?.is_admin }
          // );
          if (!isOnlyMember) {
            const apiKeysPromise = fetchApiKeys(userId);
            apiKeysPromise
              .then((apiKeysData) => {
                if (isMounted) {
                  setApiKeys(apiKeysData);
                  // console.log(`[AuthContext] API keys loaded on user update`);
                }
              })
              .catch((error) => {
                if (isMounted) {
                  setApiKeys(null);
                  // console.log(`[AuthContext] API keys failed to load:`, error);
                }
              });
          } else {
            setApiKeys(null);
            // console.log(
            //   `[AuthContext] Skipping API keys fetch for member-only`
            // );
          }
        }
      } else {
        // console.log(
        //   `[AuthContext] Event ${event} - no action needed (user data preserved)`
        // );
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Additional useEffect to refresh client memberships if empty when user is present
  useEffect(() => {
    if (user && !clientIds.length) {
      fetchClientMemberships(user.id).then(
        ({ clientIds: newIds, isOnlyMember: newIsOnly }) => {
          setClientIds(newIds);
          setIsOnlyMember(newIsOnly && !profile?.is_admin);
        },
      );
    }
  }, [user]);

  const memoizedUser = useMemo(() => user, [user?.id]);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Sign Up Failed',
        description: error.message,
      });
      return { error };
    }
    toast({
      title: 'Check your email',
      description:
        'We’ve sent you a confirmation link to complete your registration.',
    });
    return { error: null };
  };
  const signIn = async (email: string, password: string) => {
    // Clear stale session data
    localStorage.removeItem(authTokenKey);
    clearApiKeysCache();
    clearClientIdsCache();
    clearProfileCache();
    clearLegacyCaches();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      return { error };
    }
    // console.log('[AuthContext] Sign-in successful:', { userId: data.user?.id });
    // Set portal login flag if applicable
    if (window.location.pathname.startsWith('/portal')) {
      setIsPortalLogin(true);
    }
    // NEW: Removed manual getSession/set here - let onAuthStateChange handle to avoid races
    toast({
      title: 'Welcome back!',
      description: 'You have successfully signed in.',
    });
    return { error: null };
  };

  const signOut = async () => {
    if (isSigningOut) return { error: null };
    setIsSigningOut(true);
    const clearLocalState = () => {
      setUser(null);
      setSession(null);
      setProfile(null);
      setApiKeys(null);
      setClientIds([]);
      setIsOnlyMember(false);
      setIsPortalLogin(false);
      clearProfileCache();
      clearApiKeysCache();
      clearClientIdsCache();
      clearLegacyCaches();
      localStorage.removeItem(authTokenKey);
      setLoading(false);
      setIsAuthReady(true);
    };
    try {
      const { error } = await supabase.auth.signOut();
      clearLocalState();
      if (error && error.status !== 401 && error.status !== 403) {
        console.error('[AuthContext] Error signing out:', error);
        toast({
          variant: 'destructive',
          title: 'Sign Out Failed',
          description: error.message,
        });
        setIsSigningOut(false);
        return { error };
      }
      toast({
        title: 'Signed out',
        description: 'You have been successfully signed out.',
      });
      setIsSigningOut(false);
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Sign out failed:', error);
      clearLocalState();
      toast({
        title: 'Signed out',
        description: 'You have been successfully signed out.',
      });
      setIsSigningOut(false);
      return { error: null };
    }
  };

  const value: AuthContextType = {
    user: memoizedUser,
    session,
    profile,
    apiKeys,
    loading,
    hasInitialLoad,
    signUp,
    signIn,
    signOut,
    isAdmin: profile?.is_admin || false,
    isOnlyMember,
    clientIds,
    refreshProfile,
    refreshApiKeys,
    isPortalLogin,
    setIsPortalLogin,
    isAuthReady,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
